import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme';
const EVENT_NAME = 'themeChanged';

function readStoredTheme() {
    try {
        return localStorage.getItem(STORAGE_KEY) || 'light';
    } catch (error) {
        return 'light';
    }
}

/**
 * Single source of truth for the app's light/dark mode.
 *
 * Keeps the pre-existing mechanism used across the app (Settings.js,
 * Sale.js, the old Main.js/AccountMenu.js): `localStorage.theme` +
 * a `themeChanged` CustomEvent + a `storage` event fallback for other tabs.
 * This hook does not replace that mechanism, it centralizes it so the new
 * global ThemeProvider and the topbar toggle share one implementation.
 */
export function useColorMode() {
    const [mode, setMode] = useState(readStoredTheme);

    useEffect(() => {
        const handleStorageChange = () => setMode(readStoredTheme());
        const handleThemeChanged = (event) => {
            setMode(event.detail?.theme || readStoredTheme());
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(EVENT_NAME, handleThemeChanged);

        // Fallback poll, mirrors the previous implementation in Main.js so
        // any code still relying on plain localStorage writes (without
        // dispatching the custom event) keeps working.
        const interval = setInterval(() => {
            const stored = readStoredTheme();
            setMode((current) => (current === stored ? current : stored));
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(EVENT_NAME, handleThemeChanged);
            clearInterval(interval);
        };
    }, []);

    const toggleMode = useCallback(() => {
        const next = readStoredTheme() === 'dark' ? 'light' : 'dark';
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch (error) {
            // ignore storage failures (private mode, quota, etc.)
        }
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { theme: next } }));
        setMode(next);
    }, []);

    return { mode, toggleMode };
}

export default useColorMode;
